import axios from 'axios';
import { getNotificationTab } from 'utils/notificationTab';

/** Header / pill badge text: 1–99 as-is, anything higher as 99+. */
export const formatBadgeCount = (count) => {
  const n = Number(count) || 0;
  if (n <= 0) return '0';
  return n > 99 ? '99+' : String(n);
};

/** Absolute received time for notification cards, e.g. "16 Sep 2026 · 12:29 PM". */
export const formatNotificationReceivedAt = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${day} · ${time}`;
};

const isRead = (item) =>
  Boolean(item?.readAt) ||
  String(item?.status || '').toUpperCase() === 'READ' ||
  item?.read === true;

/**
 * Walk recent IN_APP notifications (up to maxPages × 100) and return unread
 * totals by tab, plus read count. Prefer the server unread-count for `unreadAll`
 * so the header bell matches the Notification Center "All" pill.
 */
export async function fetchNotificationTabCounts(receiverId) {
  const pageSize = 100;
  const maxPages = 5;
  const receiverParam = receiverId
    ? `?receiverId=${encodeURIComponent(receiverId)}`
    : '';

  const unreadRes = await axios
    .get(`v2/notifications/unread-count${receiverParam}`, {
      withCredentials: true,
    })
    .catch(() => null);
  const serverUnread = Number(unreadRes?.data?.data?.count);

  let page = 1;
  let totalPages = 1;
  const items = [];

  while (page <= totalPages && page <= maxPages) {
    const listParam = receiverId
      ? `&receiverId=${encodeURIComponent(receiverId)}`
      : '';
    const { data } = await axios.get(
      `v2/notifications?page=${page}&pageSize=${pageSize}${listParam}`,
      { withCredentials: true },
    );
    const batch = Array.isArray(data?.data?.items) ? data.data.items : [];
    items.push(...batch);
    totalPages = Math.max(1, Number(data?.data?.totalPages || 1));
    if (batch.length === 0) break;
    page += 1;
  }

  let unreadBxi = 0;
  let unreadPurchase = 0;
  let unreadSell = 0;
  let unreadFromItems = 0;

  items.forEach((raw) => {
    const item = { ...raw, read: isRead(raw) };
    if (item.read) return;
    unreadFromItems += 1;
    const tab = getNotificationTab(item);
    if (tab === 'purchase') unreadPurchase += 1;
    else if (tab === 'sell') unreadSell += 1;
    else unreadBxi += 1;
  });

  // Read badge must match the Read tab list. Counting only the scanned pages
  // missed older reads; the list API already supports status=READ with a total.
  let readCount = 0;
  try {
    const readListParam = receiverId
      ? `&receiverId=${encodeURIComponent(receiverId)}`
      : '';
    const readRes = await axios.get(
      `v2/notifications?page=1&pageSize=1&status=READ${readListParam}`,
      { withCredentials: true },
    );
    readCount = Number(readRes?.data?.data?.total) || 0;
  } catch (_) {
    readCount = items.filter((raw) => isRead(raw)).length;
  }

  return {
    unreadAll: Number.isFinite(serverUnread) ? serverUnread : unreadFromItems,
    unreadBxi,
    unreadPurchase,
    unreadSell,
    readCount,
  };
}
